import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../config";
import { honorific } from "../utils/namePrefix";
import FollowCTA from "../components/episode/FollowCTA";
import { FaArrowLeft, FaChevronLeft, FaChevronRight, FaSearch, FaPlay } from "react-icons/fa";

// All episodes (Bhaskar, 2026-10-21): every Kathakala episode as thumbnails, one
// row (carousel) per year, newest first; each opens our own episode page. Reached
// from the story and episode pages ("All episodes") and from Home. A filter box
// matches episode numbers, titles and storyteller names across all years.

const thumb = (id) => (id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : null);
const showDate = (e) => e.scheduled_start || e.publish_date || "";
const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", {
        timeZone: "America/Chicago", month: "short", day: "numeric", year: "numeric",
      })
    : "";
const yearOf = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { timeZone: "America/Chicago", year: "numeric" }) : "—";

async function fetchAll(table, select, build = (q) => q) {
  const out = [];
  for (let from = 0; ; from += 1000) {
    const { data, error } = await build(supabase.from(table).select(select)).range(from, from + 999);
    if (error) throw error;
    out.push(...(data || []));
    if (!data || data.length < 1000) return out;
  }
}

export default function EpisodesBrowse() {
  const [episodes, setEpisodes] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    document.title = "కథాకళ · All episodes | Kathakala";
    (async () => {
      const [eps, perfs] = await Promise.all([
        fetchAll("episodes", "episode_id, title, video_id, youtube_broadcast_id, publish_date, scheduled_start"),
        fetchAll("performances", "episode_id, story_start_sec, storyteller_name_telugu, storytellers(gender, storyteller_telugu)"),
      ]);
      const names = {};
      perfs
        .sort((a, b) => (a.story_start_sec ?? 1e9) - (b.story_start_sec ?? 1e9))
        .forEach((p) => {
          const st = p.storytellers || {};
          const n = honorific(st.storyteller_telugu || p.storyteller_name_telugu, st.gender);
          if (!n) return;
          const list = names[p.episode_id] || (names[p.episode_id] = []);
          if (!list.includes(n)) list.push(n);
        });
      setEpisodes(
        eps
          .filter((e) => e.video_id || e.youtube_broadcast_id)
          .map((e) => ({ ...e, vid: e.youtube_broadcast_id || e.video_id, tellers: names[e.episode_id] || [] }))
          .sort((a, b) => showDate(b).localeCompare(showDate(a)) || b.episode_id - a.episode_id)
      );
    })().catch((err) => {
      console.error("Episodes load error:", err);
      setEpisodes([]);
    });
  }, []);

  const filtered = useMemo(() => {
    if (!episodes) return [];
    const t = q.trim().toLowerCase();
    if (!t) return episodes;
    return episodes.filter(
      (e) =>
        String(e.episode_id) === t.replace(/^#|^ep(isode)?\s*/i, "") ||
        (e.title || "").toLowerCase().includes(t) ||
        e.tellers.some((n) => n.toLowerCase().includes(t))
    );
  }, [episodes, q]);

  const byYear = useMemo(() => {
    const m = new Map();
    filtered.forEach((e) => {
      const y = yearOf(showDate(e));
      if (!m.has(y)) m.set(y, []);
      m.get(y).push(e);
    });
    return [...m.entries()];
  }, [filtered]);

  return (
    <div className="min-h-screen bg-kk-ink text-white">
      <div className="bg-kk-ink/95 border-b border-kk-gold/10 sticky top-0 z-20 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2 text-kk-gold/70 hover:text-kk-gold text-sm">
            <FaArrowLeft className="text-xs" />
            <span className="font-krushna">కథాకళ</span>
          </Link>
          <span className="text-kk-cream/30">·</span>
          <span className="text-kk-cream/70 text-sm">All episodes</span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <h1 className="font-krushna text-kk-gold text-2xl md:text-3xl">కథాకళ ఎపిసోడ్‌లు</h1>
        <p className="text-kk-cream/60 text-sm mb-4">
          All episodes{episodes ? ` · ${episodes.length}` : ""} · LIVE every Saturday, 8 PM CT
        </p>

        <div className="relative max-w-xl mb-6">
          <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-kk-gold/60" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Episode number or storyteller · ఎపిసోడ్ లేదా కథకులు"
            className="w-full bg-white/[0.06] border border-kk-gold/30 rounded-full py-3 pl-11 pr-4 text-kk-cream placeholder-kk-gold-light/50 focus:outline-none focus:border-kk-gold/70"
          />
        </div>

        {!episodes && (
          <div className="flex justify-center py-16">
            <div className="w-12 h-12 border-4 border-kk-gold/30 border-t-kk-gold rounded-full animate-spin" />
          </div>
        )}
        {episodes && filtered.length === 0 && (
          <p className="text-kk-cream/50 py-10 text-center">No episodes match “{q}”.</p>
        )}

        <div className="space-y-8">
          {byYear.map(([year, list]) => (
            <YearRow key={year} year={year} list={list} />
          ))}
        </div>

        <div className="mt-10">
          <FollowCTA shareTitle="కథాకళ · All episodes" />
        </div>
      </div>
    </div>
  );
}

const YearRow = ({ year, list }) => {
  const ref = useRef(null);
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * ref.current.clientWidth * 0.85, behavior: "smooth" });
  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-kk-gold-light text-lg font-semibold">
          {year} <span className="text-kk-cream/40 text-sm font-normal">· {list.length}</span>
        </h2>
        <div className="hidden sm:flex gap-2">
          <button type="button" onClick={() => scroll(-1)} aria-label={`Earlier in ${year}`}
            className="w-8 h-8 rounded-full border border-kk-gold/25 text-kk-gold/70 hover:text-kk-gold hover:border-kk-gold/60 flex items-center justify-center">
            <FaChevronLeft className="text-xs" />
          </button>
          <button type="button" onClick={() => scroll(1)} aria-label={`Later in ${year}`}
            className="w-8 h-8 rounded-full border border-kk-gold/25 text-kk-gold/70 hover:text-kk-gold hover:border-kk-gold/60 flex items-center justify-center">
            <FaChevronRight className="text-xs" />
          </button>
        </div>
      </div>
      <div ref={ref} className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3">
        {list.map((e) => (
          <Link
            key={e.episode_id}
            to={`/episodes/${e.episode_id}`}
            className="group snap-start flex-shrink-0 w-[220px] md:w-[250px]"
          >
            <div className="relative rounded-lg overflow-hidden border border-kk-gold/15 group-hover:border-kk-gold/50 transition-colors">
              <img src={thumb(e.vid)} alt={`Episode ${e.episode_id}`} loading="lazy"
                className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-500" />
              <span className="absolute top-1.5 left-1.5 text-[11px] font-semibold bg-black/70 text-kk-gold px-2 py-0.5 rounded">
                #{e.episode_id}
              </span>
              <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors">
                <span className="bg-red-600 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <FaPlay className="text-white text-xs ml-0.5" />
                </span>
              </span>
            </div>
            <p className="text-kk-gold-light/60 text-xs mt-1.5">{fmtDate(showDate(e))}</p>
            <p className="font-krushna text-kk-cream/85 text-sm leading-snug line-clamp-2 group-hover:text-kk-gold-light">
              {e.tellers.length ? e.tellers.join(", ") : e.title || `Episode ${e.episode_id}`}
            </p>
          </Link>
        ))}
      </div>
    </section>
  );
};
